import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * One anchored, portalled panel, for every control in the app that opens one.
 *
 * There were two of these, written months apart and drifted: the calendar's in
 * DatePicker.jsx and the dropdown's in Dropdown.jsx. They agreed on the hard part — portal to
 * the body, position from the trigger's own rect, flip above when there is no room below — and
 * disagreed on everything else, including one thing that was a bug rather than a preference:
 * the calendar stopped Escape from propagating and the dropdown did not, so pressing Escape on
 * any PickerDropdown inside a modal closed the modal as well as the list.
 *
 * This is the merge. The calendar's capture-phase Escape and hide-until-measured, the
 * dropdown's left/right alignment and match-the-trigger width. Everything that opens a panel
 * uses it, so the next fix lands in one place.
 *
 * Why portalled and fixed rather than absolutely positioned beside the trigger — both reasons
 * were learned the hard way here and neither is obvious:
 *
 *   The topbar sets `backdrop-filter`, which makes it a containing block. A `position: fixed`
 *   child of anything inside it is positioned against the topbar, not the viewport.
 *
 *   `.modal` scrolls its own body, so a panel that has to overhang the dialog is clipped by
 *   it. The roles picker hung 82px past the bottom of Edit person and was simply cut off.
 */

const GAP = 6   // between the trigger and the panel
const EDGE = 8  // the closest the panel may come to the window's edge

/**
 * Where the panel goes, remeasured whenever that could have changed.
 *
 * `place` is returned as well as the position because some panels change size after they
 * open — a filtered list is shorter than the list it filtered — and the caller is the only
 * one that knows when.
 */
export function useAnchoredPanel(open, anchorRef, panelRef, { align = 'left', matchWidth = false } = {}) {
  const [pos, setPos] = useState(null)

  const place = useCallback(() => {
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor) return
    const a = anchor.getBoundingClientRect()
    // Fall back to a plausible calendar's size on the first pass, before the panel exists to
    // be measured; the layout effect below runs again once it does.
    const w = panel?.offsetWidth || 300
    const h = panel?.offsetHeight || 340

    // Below by default, flipped above only when there is genuinely no room. A panel that
    // opens upward when it did not need to reads as a glitch.
    const below = a.bottom + GAP
    const top = below + h <= window.innerHeight - EDGE ? below : Math.max(EDGE, a.top - GAP - h)
    const wanted = align === 'left' ? a.left : a.right - w
    const left = Math.max(EDGE, Math.min(wanted, window.innerWidth - w - EDGE))
    setPos({ top, left, minWidth: matchWidth ? a.width : undefined })
  }, [anchorRef, panelRef, align, matchWidth])

  // Before paint, so the panel is never visible in the wrong place for a frame first.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined }
    place()
    // `true` for capture: the board header and a modal body both scroll without bubbling to
    // window, and a panel that stays put while its trigger scrolls away is worse than one
    // that closes.
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  return { pos, place }
}

/**
 * Closes on an outside click or Escape, counting the portalled panel as inside.
 *
 * Escape is handled in the CAPTURE phase and stopped there. Modal.jsx has its own
 * document-level Escape handler, so without this a picker open inside a dialog would close
 * the dialog underneath it — losing whatever had been typed — instead of just closing itself.
 */
export function useDismiss(open, close, anchorRef, panelRef) {
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (anchorRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      close()
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, close, anchorRef, panelRef])
}

/**
 * The panel itself.
 *
 * `placeKey` is for content that changes size while open: change it and the panel is measured
 * and placed again. `matchWidth` pins the panel to at least its trigger's width, which a list
 * under a wide button wants and a calendar does not.
 */
export default function Popover({
  open,
  anchorRef,
  close,
  className = '',
  role = 'dialog',
  label,
  align = 'left',
  matchWidth = false,
  placeKey,
  // On the panel rather than on whichever child happens to have focus. A list with a search
  // box has two candidates and the arrow keys have to work from either, and putting the
  // handler on each of them means the one nobody focused is the one that was needed.
  onKeyDown,
  // For a panel that opens on hover: the portal means it is no longer a DOM descendant of
  // its trigger, so a mouseleave on the trigger's container never hears about the pointer
  // arriving here. Put on the panel itself rather than on a wrapper inside it, so the
  // padding around the rows counts as being on the menu — crossing it is how you reach the
  // first row.
  onMouseEnter,
  onMouseLeave,
  // Move focus into the panel once it opens. Off by default — the calendar deliberately
  // leaves focus on its trigger until somebody presses an arrow key — and on for the lists,
  // where the arrow keys are the point.
  autoFocus = false,
  children,
}) {
  const panelRef = useRef(null)
  const { pos, place } = useAnchoredPanel(open, anchorRef, panelRef, { align, matchWidth })
  useDismiss(open, close, anchorRef, panelRef)

  useEffect(() => { if (open) place() }, [open, placeKey, place])

  /**
   * Focus goes in only once the panel has been placed.
   *
   * Until then it is `visibility: hidden` so it cannot be seen at -9999, and a hidden element
   * cannot take focus — a .focus() call before this point returns silently and the arrow keys
   * then go to the document, which is exactly what happened when the dropdowns tried to do
   * this for themselves.
   */
  useEffect(() => {
    if (!open || !autoFocus || !pos) return
    const panel = panelRef.current
    if (!panel || panel.contains(document.activeElement)) return
    const first = panel.querySelector('input, [tabindex]:not([tabindex="-1"]), button')
    ;(first || panel).focus?.()
  }, [open, autoFocus, Boolean(pos)])

  /**
   * Put focus back where it came from, but only if it is currently inside the panel.
   *
   * Somebody who closed this by clicking a different control has already chosen where focus
   * should be, and yanking it back to the trigger would undo that. Somebody who closed it
   * with Escape or by picking an option has focus in a panel that is about to stop existing,
   * and gets it back on the trigger.
   */
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wasOpen.current && !open) {
      const inPanel = panelRef.current?.contains(document.activeElement)
      const stranded = !document.activeElement || document.activeElement === document.body
      if (inPanel || stranded) {
        const anchor = anchorRef.current
        const target = anchor?.matches?.('button, [tabindex]') ? anchor : anchor?.querySelector('button, [tabindex]')
        target?.focus?.()
      }
    }
    wasOpen.current = open
  }, [open, anchorRef])

  if (!open) return null

  return createPortal(
    <div
      ref={panelRef}
      className={`popover ${className}`}
      role={role}
      aria-label={label}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        minWidth: pos?.minWidth,
        // Hidden rather than unrendered until it has been measured: it has to be in the
        // document to have a size, and it must not be seen at -9999 on the way there.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
